import type { CampaignOffer, SampleCustomer, StudioChannel } from "./types";

export type TemplateVariable = {
  key: string;
  // translation key for the chip label
  label: string;
  channels: StudioChannel[];
};

const ALL: StudioChannel[] = ["email", "whatsapp", "sms", "push"];

export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  { key: "first_name", label: "campaigns.studio_vars.first_name", channels: ALL },
  { key: "last_name", label: "campaigns.studio_vars.last_name", channels: ALL },
  { key: "shop_name", label: "campaigns.studio_vars.shop_name", channels: ALL },
  { key: "offer", label: "campaigns.studio_vars.offer", channels: ALL },
  { key: "coupon_code", label: "campaigns.studio_vars.coupon_code", channels: ALL },
  { key: "expiry_date", label: "campaigns.studio_vars.expiry_date", channels: ALL },
  { key: "points_balance", label: "campaigns.studio_vars.points_balance", channels: ALL },
  { key: "favourite_item", label: "campaigns.studio_vars.favourite_item", channels: ALL },
  { key: "shop_address", label: "campaigns.studio_vars.shop_address", channels: ["email"] },
];

export type RenderContext = {
  customer: SampleCustomer;
  offer: CampaignOffer;
  // Already-localized offer description, e.g. "15% off".
  offerText: string;
  shop: { name: string; address: string };
  formatDate: (date: Date) => string;
  now?: Date;
};

// Stable fake coupon so previews don't flicker between renders.
export const sampleCouponCode = (customerId: string) =>
  `LMN-${customerId.toUpperCase().padEnd(4, "X").slice(0, 4)}7Q2`;

export const variableValues = (ctx: RenderContext): Record<string, string> => {
  const now = ctx.now ?? new Date();
  const expiry =
    ctx.offer.type === "coupon" && ctx.offer.expires_in_days
      ? ctx.formatDate(new Date(now.getTime() + ctx.offer.expires_in_days * 86_400_000))
      : "";
  return {
    first_name: ctx.customer.first_name,
    last_name: ctx.customer.last_name,
    shop_name: ctx.shop.name,
    shop_address: ctx.shop.address,
    offer: ctx.offerText,
    coupon_code: ctx.offer.type === "coupon" ? sampleCouponCode(ctx.customer.id) : "",
    expiry_date: expiry,
    points_balance: String(ctx.customer.points_balance),
    favourite_item: ctx.customer.favourite_item,
  };
};

const VARIABLE_PATTERN = /\{\{\s*([a-z_]+)\s*\}\}/g;

// Unknown variables are left untouched so typos stay visible in the preview.
export const renderTemplate = (template: string, values: Record<string, string>) =>
  template.replace(VARIABLE_PATTERN, (match, key: string) =>
    key in values ? values[key] : match,
  );

export const unknownVariables = (template: string): string[] => {
  const known = new Set(TEMPLATE_VARIABLES.map((v) => v.key));
  const found = new Set<string>();
  for (const [, key] of template.matchAll(VARIABLE_PATTERN)) {
    if (!known.has(key)) found.add(key);
  }
  return [...found];
};

// ----------------------------
// SMS segmentation
// ----------------------------

// GSM 03.38 basic set plus the extension table (extension chars cost 2 septets).
const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENDED = "^{}\\[~]|€\f";

export type SmsSegmentInfo = {
  encoding: "GSM-7" | "UCS-2";
  units: number; // septets for GSM-7, UTF-16 code units for UCS-2
  segments: number;
  perSegment: number;
  remaining: number;
  nonGsmChars: string[];
};

export const smsSegments = (text: string): SmsSegmentInfo => {
  let septets = 0;
  const nonGsm = new Set<string>();
  for (const ch of text) {
    if (GSM_BASIC.includes(ch)) septets += 1;
    else if (GSM_EXTENDED.includes(ch)) septets += 2;
    else nonGsm.add(ch);
  }

  if (nonGsm.size === 0) {
    const single = 160;
    const multi = 153;
    const segments = septets <= single ? 1 : Math.ceil(septets / multi);
    const perSegment = segments > 1 ? multi : single;
    return {
      encoding: "GSM-7",
      units: septets,
      segments,
      perSegment,
      remaining: segments * perSegment - septets,
      nonGsmChars: [],
    };
  }

  const units = text.length; // UTF-16 code units, emoji count as 2
  const single = 70;
  const multi = 67;
  const segments = units <= single ? 1 : Math.ceil(units / multi);
  const perSegment = segments > 1 ? multi : single;
  return {
    encoding: "UCS-2",
    units,
    segments,
    perSegment,
    remaining: segments * perSegment - units,
    nonGsmChars: [...nonGsm],
  };
};

export const SMS_OPT_OUT_SUFFIX = "\nReply STOP to opt out";
